import { Stack, useTheme } from "@mui/material";
import { Users } from "lucide-react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { LifeInsuranceDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	BasicInfo,
	hasBasicInfo,
} from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { ValueChartSection } from "web_myplann/Portfolio/Components/PortfolioSections/ValueChartSection";
import { InvestmentTool } from "web_myplann/Portfolio/Components/PortfolioTools/InvestmentTool/InvestmentTool";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { insuredPersonsWithRisks } from "web_myplann/Portfolio/Helpers/insuredPersonsWithRisks";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { DataRowCollapsible } from "web_myplann/Shared/Components/DataRowCollapsible";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const LifeInsurance = ({
	baseData,
	currencySign,
}: PortfolioDetailProps) => {
	const { t } = useResource();
	const theme = useTheme();
	const tools = normalizeTools(baseData.tools);

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const portfolioData = baseData.data as LifeInsuranceDetailDataDTO;

	const insuredPersons = insuredPersonsWithRisks(portfolioData.insuredPersons);

	const graphData = calculateGraphData(
		reporting?.reportingData,
		reporting?.summary ?? undefined,
	);

	const hasChart = graphData.daysData.length > 0;

	const insuredAmount = portfolioData.insurance
		? getPortfolioFrequencyValue(
				portfolioData.insurance ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const employersBonus = portfolioData.employerContribution
		? getPortfolioFrequencyValue(
				portfolioData.employerContribution ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const basicInfoItems: DataRowCardItem[] = [
		{ label: t(Resources.Common.FirstName), value: baseData.clientName },
		{
			label: t(PageResources.SharedLabels.contractNumber),
			value: baseData.contractNumber,
		},
		{
			label: t(PageResources.SharedLabels.producent),
			value: baseData.producer,
		},
		{ label: t(PageResources.SharedLabels.product), value: baseData.product },
		{
			label: t(PageResources.LifeInsurance.insuredAmount),
			value: insuredAmount,
		},
		{
			label: t(PageResources.LifeInsurance.employersBonus),
			value: employersBonus,
		},
	];

	return (
		<Stack>
			<DataCard
				icon={<Users size={theme.iconSize.sm} />}
				label={t(PageResources.LifeInsurance.EnsuredPeople.title)}
				isEmpty={insuredPersons.length === 0}
				noDivider={!hasChart}
			>
				{insuredPersons.map(({ person, risks }) => (
					<DataRowCollapsible
						label={`${person.firstname} ${person.lastname} (${person.identificationNumber})`}
						key={person.identificationNumber}
					>
						{risks.map(({ risk, value }) => (
							<Stack key={risk.risk}>
								<DataRow
									label={t(PageResources.LifeInsurance.Risk[risk.risk])}
									value={moneyFormatter(value, currencySign)}
									valueTooltip={`${t(PageResources.SharedLabels.untilAge, {
										count: risk.toAge ?? 0,
									})} \u00B7 ${t(
										PageResources.LifeInsurance.EnsuredPeople.Variant[
											risk.variant
										],
									).toLowerCase()}`}
								/>
							</Stack>
						))}
					</DataRowCollapsible>
				))}
			</DataCard>

			{hasChart && (
				<ValueChartSection
					baseData={baseData}
					summary={reporting?.summary}
					interpolatedRanges={reporting?.interpolatedRanges}
					graphData={graphData}
					currencySign={currencySign}
				/>
			)}

			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={
					hasBasicInfo(baseData) ? <BasicInfo data={baseData} /> : null
				}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related, tools)
						.hasAny && tools.length === 0
				}
				items={basicInfoItems}
			/>

			<InvestmentTool
				tools={tools}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				portfolioId={baseData.id}
				title={t(PageResources.LifeInsurance.InvestmentTool.title)}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
